import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Check, ArrowLeft } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { SERVICES, PROCESS } from "../data/site";
import { Reveal } from "../components/Reveal";
import { PageHero, SectionLabel, ContactCTA } from "../components/Shared";

export default function ServeiDetall() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  const index = SERVICES.findIndex((s) => s.slug === slug);

  useEffect(() => {
    if (service) document.title = `${service.title} — Orbis Forum Català`;
  }, [service]);

  if (!service) return <Navigate to="/serveis" replace />;

  return (
    <div data-testid="service-detail">
      <PageHero
        label={`Servei ${String(index + 1).padStart(2, "0")}`}
        title={service.title}
        subtitle={service.short}
        image={service.image}
      />

      <section className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <Reveal className="lg:col-span-4">
              <SectionLabel>Descripció</SectionLabel>
              <Link
                to="/serveis"
                className="mt-8 hidden lg:inline-flex items-center gap-2 text-sm text-white/50 hover:text-ofc-gold transition-colors"
              >
                <ArrowLeft size={15} strokeWidth={1.5} /> Tots els serveis
              </Link>
            </Reveal>
            <div className="lg:col-span-8 space-y-6">
              {service.intro.map((p, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <p
                    className={
                      i === 0
                        ? "font-serif-display font-light text-2xl md:text-3xl leading-snug tracking-tight text-white/90 text-balance"
                        : "text-white/55 font-light leading-relaxed"
                    }
                  >
                    {p}
                  </p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BENEFICIS */}
      <section className="border-t border-white/5 bg-ofc-navy/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <SectionLabel>Beneficis</SectionLabel>
            <h2 className="font-serif-display font-light text-3xl md:text-5xl tracking-tight mt-6">
              Què aporta aquest servei
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 mt-14">
            {service.benefits.map((b, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="flex items-start gap-4 border-b border-white/5 pb-6">
                  <Check size={18} strokeWidth={1.5} className="text-ofc-gold mt-1 shrink-0" />
                  <p className="text-white/70 font-light leading-relaxed">{b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCÉS */}
      <section className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <SectionLabel>Procés</SectionLabel>
            <h2 className="font-serif-display font-light text-3xl md:text-5xl tracking-tight mt-6">
              Com abordem el vostre cas
            </h2>
          </Reveal>
          <div className="mt-14 divide-y divide-white/5 border-y border-white/5">
            {PROCESS.map((p, i) => (
              <Reveal key={p.step} delay={i * 0.06}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 py-8">
                  <span className="md:col-span-2 font-serif-display text-3xl text-ofc-gold/70">{p.step}</span>
                  <h3 className="md:col-span-3 font-serif-display text-2xl">{p.title}</h3>
                  <p className="md:col-span-7 text-white/50 font-light leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-white/5 bg-ofc-navy/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <Reveal className="lg:col-span-4">
              <SectionLabel>Preguntes freqüents</SectionLabel>
              <h2 className="font-serif-display font-light text-3xl md:text-4xl tracking-tight mt-6">
                Els dubtes més habituals
              </h2>
            </Reveal>
            <div className="lg:col-span-8">
              <Reveal>
                <Accordion type="single" collapsible className="w-full">
                  {service.faq.map((f, i) => (
                    <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
                      <AccordionTrigger
                        data-testid={`faq-trigger-${i}`}
                        className="text-left font-serif-display text-xl md:text-2xl text-white hover:text-ofc-gold hover:no-underline py-6"
                      >
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-white/55 font-light leading-relaxed text-base pb-6">
                        {f.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <ContactCTA
        title={`Parlem del vostre projecte de ${service.title.toLowerCase()}`}
        text="Us oferim una primera consulta reservada per valorar com podem acompanyar-vos."
      />
    </div>
  );
}
